import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Activity, TrendingUp, TrendingDown, Loader2, BarChart3, Save, History, Trash2, Upload, TableIcon, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { PortfolioFileImport, ImportedPosition } from "@/components/PortfolioFileImport";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";

interface SavedSnapshot {
  id: string;
  created_at: string;
  positions_count: number;
  total_value: number;
  positions_data: any[];
  analysis_data: any;
}

export default function PerformanceTracker() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dbPositions, setDbPositions] = useState<any[]>([]);
  const [importedPositions, setImportedPositions] = useState<ImportedPosition[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [savedSnapshots, setSavedSnapshots] = useState<SavedSnapshot[]>([]);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState<'db' | 'import'>('db');
  const [sortCol, setSortCol] = useState<'name' | 'invested' | 'value' | 'gain' | 'addedValue'>('gain');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [returnFilter, setReturnFilter] = useState<'all' | 'positive' | 'negative'>('all');

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortCol }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 inline" /> : <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  // Generate a small sparkline SVG from invested → current value with seeded randomness
  const MiniSparkline = ({ invested, value, gain }: { invested: number; value: number; gain: number }) => {
    const w = 64, h = 20, pts = 8;
    const points = useMemo(() => {
      const arr: number[] = [invested];
      const seed = Math.abs(invested * 7 + value * 13) % 1000;
      for (let i = 1; i < pts - 1; i++) {
        const progress = i / (pts - 1);
        const base = invested + (value - invested) * progress;
        const noise = (((seed * (i + 1) * 9301 + 49297) % 233280) / 233280 - 0.5) * Math.abs(value - invested) * 0.4;
        arr.push(base + noise);
      }
      arr.push(value);
      return arr;
    }, [invested, value]);

    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const pathD = points.map((v, i) => {
      const x = (i / (pts - 1)) * w;
      const y = h - 2 - ((v - min) / range) * (h - 4);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');

    const color = gain >= 0 ? 'hsl(var(--chart-2))' : 'hsl(var(--destructive))';
    return (
      <svg width={w} height={h} className="inline-block">
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadData();
    loadSnapshots();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    const { data } = await supabase.from("positions").select("*").eq("user_id", user.id);
    setDbPositions(data || []);
    setLoading(false);
  };

  const loadSnapshots = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("portfolio_snapshots")
      .select("*")
      .eq("user_id", user.id)
      .eq("snapshot_type", "performance")
      .order("created_at", { ascending: false })
      .limit(10);
    setSavedSnapshots((data as any[]) || []);
  }, [user]);

  const handleImport = (positions: ImportedPosition[]) => {
    setImportedPositions(positions);
    setDataSource('import');
    setShowImport(false);
  };

  const activePositions = dataSource === 'import' ? importedPositions : dbPositions;

  const stats = useMemo(() => {
    if (activePositions.length === 0) return null;
    let totalInvested = 0;
    let totalValue = 0;
    let totalAddedValue = 0;

    const positionPerformance = activePositions.map((p: any) => {
      const value = dataSource === 'import'
        ? (p.totalValue || 0)
        : (p.total_value ? Number(p.total_value) : Number(p.buy_price) * Number(p.quantity));

      // Use BR column performance when available (Excel import)
      let gain: number;
      if (dataSource === 'import' && p.brPerformance != null && p.brPerformance !== 0) {
        const brVal = p.brPerformance;
        gain = Math.abs(brVal) > 1 ? brVal : brVal * 100;
      } else if (dataSource === 'import') {
        gain = p.gainPct * 100;
      } else {
        const inv = Number(p.buy_price) * Number(p.quantity);
        gain = p.gain_pct ? Number(p.gain_pct) * 100 : (inv > 0 ? ((value - inv) / inv) * 100 : 0);
      }

      // Derive invested from current value and gain% for consistency
      const invested = gain !== -100 ? value / (1 + gain / 100) : value;
      const addedValue = value - invested;

      totalInvested += invested;
      totalValue += value;
      totalAddedValue += addedValue;

      return {
        name: (dataSource === 'import' ? p.stockName : (p.stock_name || p.ticker || "")).substring(0, 20),
        ticker: p.ticker,
        invested: Math.round(invested),
        value: Math.round(value),
        gain: Math.round(gain * 100) / 100,
        addedValue: Math.round(addedValue),
        currency: dataSource === 'import' ? (p.localCurrency || 'CHF') : (p.local_currency || 'CHF'),
        brRaw: dataSource === 'import' ? p.brPerformance : null,
      };
    });

    const overallReturn = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested) * 100 : 0;

    return {
      totalInvested: Math.round(totalInvested),
      totalValue: Math.round(totalValue),
      totalAddedValue: Math.round(totalAddedValue),
      overallReturn: Math.round(overallReturn * 100) / 100,
      positionPerformance: positionPerformance.sort((a, b) => b.gain - a.gain),
      bestPerformer: [...positionPerformance].sort((a, b) => b.gain - a.gain)[0],
      worstPerformer: [...positionPerformance].sort((a, b) => a.gain - b.gain)[0],
    };
  }, [activePositions, dataSource]);

  const saveSnapshot = async () => {
    if (!user || !stats) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("portfolio_snapshots").insert({
        user_id: user.id,
        snapshot_type: "performance",
        positions_data: activePositions as any,
        analysis_data: { ...stats, source: dataSource } as any,
        metadata: { source: dataSource } as any,
        positions_count: activePositions.length,
        total_value: stats.totalValue,
      });
      if (error) throw error;
      toast({ title: "Saved", description: "Performance analysis saved." });
      loadSnapshots();
    } catch (e: any) {
      toast({ title: "Save Failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const restoreSnapshot = (snap: SavedSnapshot) => {
    const analysis = snap.analysis_data as any;
    if (analysis?.source === 'import') {
      setImportedPositions(snap.positions_data as ImportedPosition[]);
      setDataSource('import');
    } else {
      setDbPositions(snap.positions_data);
      setDataSource('db');
    }
    toast({ title: "Restored", description: `Loaded from ${format(new Date(snap.created_at), "dd MMM yyyy HH:mm")}` });
  };

  const deleteSnapshot = async (id: string) => {
    await supabase.from("portfolio_snapshots").delete().eq("id", id);
    setSavedSnapshots(prev => prev.filter(s => s.id !== id));
    toast({ title: "Deleted" });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/10 rounded-lg">
                <Activity className="h-5 w-5 text-blue-500" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Performance Tracker</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stats && (
              <Button variant="outline" size="sm" onClick={saveSnapshot} disabled={saving} className="gap-2">
                <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} className="gap-2">
              <Upload className="h-4 w-4" />Import
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        <Collapsible open={showImport} onOpenChange={setShowImport}>
          <CollapsibleContent>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Import Portfolio Data</CardTitle>
                <CardDescription>Upload Excel (PRVFIN) or IBKR file</CardDescription>
              </CardHeader>
              <CardContent>
                <PortfolioFileImport onImportComplete={handleImport} buttonLabel="Import for Performance Analysis" />
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {savedSnapshots.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" /> Saved Analyses ({savedSnapshots.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {savedSnapshots.map(snap => (
                  <div key={snap.id} className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => restoreSnapshot(snap)} className="text-xs">
                      {format(new Date(snap.created_at), "dd MMM HH:mm")} • {snap.positions_count} pos
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSnapshot(snap.id)}>
                      <Trash2 className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !stats ? (
          <Card className="text-center py-16">
            <CardContent>
              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Position Data</h2>
              <p className="text-muted-foreground mb-4">Import positions via Excel or IBKR to see performance metrics.</p>
              <Button onClick={() => setShowImport(true)} className="gap-2"><Upload className="h-4 w-4" />Import Data</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {dataSource === 'import' && <Badge variant="outline" className="text-xs">From imported file • {importedPositions.length} positions</Badge>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Total Invested</p><p className="text-2xl font-bold text-foreground">{stats.totalInvested.toLocaleString()} CHF</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Current Value</p><p className="text-2xl font-bold text-foreground">{stats.totalValue.toLocaleString()} CHF</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Total Return</p><p className={`text-2xl font-bold ${stats.overallReturn >= 0 ? "text-emerald-500" : "text-destructive"}`}>{stats.overallReturn >= 0 ? "+" : ""}{stats.overallReturn}%</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Added Value</p><p className={`text-2xl font-bold ${stats.totalAddedValue >= 0 ? "text-emerald-500" : "text-destructive"}`}>{stats.totalAddedValue >= 0 ? "+" : ""}{stats.totalAddedValue.toLocaleString()} CHF</p></CardContent></Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.bestPerformer && (
                <Card className="border-emerald-500/20">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" /> Best Performer</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{stats.bestPerformer.name}</span>
                      <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">+{stats.bestPerformer.gain}%</Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
              {stats.worstPerformer && (
                <Card className="border-destructive/20">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="h-4 w-4 text-destructive" /> Worst Performer</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{stats.worstPerformer.name}</span>
                      <Badge variant="destructive">{stats.worstPerformer.gain}%</Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Return by Position (%)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={stats.positionPerformance} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={75} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                    <Bar dataKey="gain" name="Return %">
                      {stats.positionPerformance.map((entry: any, i: number) => (
                        <Cell key={i} fill={entry.gain >= 0 ? "hsl(var(--primary))" : "hsl(var(--destructive))"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Performance Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TableIcon className="h-4 w-4 text-primary" /> Performance by Position
                  </CardTitle>
                  <div className="flex gap-1">
                    {(['all', 'positive', 'negative'] as const).map(f => (
                      <Button key={f} size="sm" variant={returnFilter === f ? 'default' : 'outline'}
                        className="h-7 text-xs px-2" onClick={() => setReturnFilter(f)}>
                        {f === 'all' ? 'All' : f === 'positive' ? '▲ Gains' : '▼ Losses'}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')}>
                          Name <SortIcon col="name" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('invested')}>
                          Invested (CHF) <SortIcon col="invested" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('value')}>
                          Current (CHF) <SortIcon col="value" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('gain')}>
                          Return % <SortIcon col="gain" />
                        </TableHead>
                        <TableHead className="text-center">Trend</TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('addedValue')}>
                          Net Gain/Loss (CHF) <SortIcon col="addedValue" />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...stats.positionPerformance]
                        .filter((pos: any) => returnFilter === 'all' ? true : returnFilter === 'positive' ? pos.gain >= 0 : pos.gain < 0)
                        .sort((a: any, b: any) => {
                          const aVal = sortCol === 'name' ? a.name.toLowerCase() : a[sortCol];
                          const bVal = sortCol === 'name' ? b.name.toLowerCase() : b[sortCol];
                          if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                          if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                          return 0;
                        }).map((pos: any, i: number) => (
                        <TableRow key={pos.ticker || i}>
                          <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                          <TableCell className="font-medium text-sm">{pos.name}</TableCell>
                          <TableCell className="text-right text-sm">{pos.invested.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-sm">{pos.value.toLocaleString()}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className={pos.gain >= 0
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                              : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                            }>
                              {pos.gain >= 0 ? "+" : ""}{pos.gain}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <MiniSparkline invested={pos.invested} value={pos.value} gain={pos.gain} />
                          </TableCell>
                          <TableCell className={`text-right font-semibold text-sm ${pos.addedValue >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                            {pos.addedValue >= 0 ? "+" : ""}{pos.addedValue.toLocaleString()} CHF
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/30 font-bold">
                        <TableCell></TableCell>
                        <TableCell className="font-bold">Total</TableCell>
                        <TableCell className="text-right font-bold">{stats.totalInvested.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-bold">{stats.totalValue.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className={stats.overallReturn >= 0
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                            : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                          }>
                            {stats.overallReturn >= 0 ? "+" : ""}{stats.overallReturn}%
                          </Badge>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className={`text-right font-bold ${stats.totalAddedValue >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                          {stats.totalAddedValue >= 0 ? "+" : ""}{stats.totalAddedValue.toLocaleString()} CHF
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-blue-500" /> Net Gain / Loss per Position (CHF)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(350, stats.positionPerformance.length * 28)}>
                  <BarChart data={stats.positionPerformance} layout="vertical" margin={{ left: 90, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toLocaleString()}`} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={85} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }}
                      formatter={(v: any) => [`${Number(v) >= 0 ? '+' : ''}${Number(v).toLocaleString()} CHF`, 'Net Gain/Loss']}
                    />
                    <Bar dataKey="addedValue" name="Net Gain/Loss (CHF)" radius={[0, 4, 4, 0]}>
                      {stats.positionPerformance.map((entry: any, i: number) => (
                        <Cell key={i} fill={entry.addedValue >= 0 ? "hsl(142 76% 36%)" : "hsl(var(--destructive))"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
